/**
 * Familienkonto-Daten-Adapter (CRM 2.0, Batch-13 Paket E — Familienkonto-Zentrierung)
 *
 * Konto-weite Sicht auf Aufgaben/Notizen/Nachrichten/Protokoll: eine Zeile
 * gehört zum Konto, wenn `account_id` gesetzt ist ODER `user_id` zu einem
 * Mitglied des Kontos gehört. `user_id` bleibt je Zeile eine OPTIONALE
 * Personenzuordnung (Default beim Anlegen: keiner ausgewählt → Konto-weiter
 * Eintrag) und wird als `user_name` mitgeliefert (Chip "Person: Vorname
 * Nachname" im UI).
 *
 * Backend-Vertrag (`{ data, meta, errors }`-Envelope, wie familyAccounts.ts):
 *   GET    /v2/admin/family-accounts/{id}/tasks       (permission users.read)
 *   POST   /v2/admin/family-accounts/{id}/tasks       (permission users.write)
 *   PATCH  /v2/admin/tasks/{id}
 *   DELETE /v2/admin/tasks/{id}
 *
 *   GET    /v2/admin/family-accounts/{id}/notices
 *   POST   /v2/admin/family-accounts/{id}/notices
 *   PATCH  /v2/admin/notices/{id}
 *   PATCH  /v2/admin/notices/{id}/pin
 *   DELETE /v2/admin/notices/{id}
 *
 *   GET    /v2/admin/family-accounts/{id}/messages
 *   POST   /v2/admin/users/{id}/messages   (personenbezogen — löst tatsächlich
 *                                            eine Mail aus, siehe Backend)
 *
 *   GET    /v2/admin/family-accounts/{id}/protocol    (permission protokoll.read)
 *
 * v1 bleibt komplett unberührt — dies ist ein additiver v2-Weg auf denselben
 * Tabellen (`tasks`, `notices`, `messages`). Siehe `src/lib/v1.ts` (v1Tasks,
 * v1Notices, v1Messages) für den bisherigen personenbezogenen Weg.
 */

import { apiV2 } from './api';
import type { V2Meta, V2Paginated } from './familyAccounts';
import type { ActivityProtocolEntry } from './activityProtocol';

/* ─── Domänen-Typen ──────────────────────────────────────────────────────── */

export interface AccountTaskRow {
  id: number;
  user_id: number | null;
  account_id: number | null;
  /** Anzeigename der zugeordneten Person, oder null (Konto-weiter Eintrag). */
  user_name: string | null;
  von?: number | string | null;
  bearbeiter?: number[] | null;
  bearbeiter_gruppen?: number[] | null;
  titel?: string;
  beschreibung?: string;
  zieldatum?: string | null;
  priority?: number;
  status?: number;
  created_at?: string;
  updated_at?: string;
  activities?: unknown[];
  [key: string]: unknown;
}

export interface AccountNoticeRow {
  id: number;
  user_id: number | null;
  account_id: number | null;
  user_name: string | null;
  admin_id?: number | null;
  comment?: string;
  pinned?: number | boolean;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

export interface AccountMessageRow {
  id: number;
  user_id: number | null;
  account_id: number | null;
  user_name: string | null;
  status?: number;
  betreff?: string;
  nachricht?: string;
  empfaenger?: string;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

export interface AccountListParams {
  page?: number;
  per_page?: number;
}

interface Envelope<T> {
  data: T;
  meta: unknown;
  errors: { code: string; message: string }[] | null;
}

function unwrapPage<T>(res: { data: { data: T[]; meta: V2Meta } }): V2Paginated<T> {
  return { data: res.data.data ?? [], meta: res.data.meta };
}

/* ─── Tasks ──────────────────────────────────────────────────────────────── */

export interface CreateAccountTaskPayload {
  titel: string;
  beschreibung: string;
  /** Optional — Default: kein Personenbezug (Konto-weite Aufgabe). */
  user_id?: number | null;
  zieldatum?: string | null;
  bearbeiter?: number[] | null;
  bearbeiter_gruppen?: number[] | null;
  priority?: number;
  status?: number;
}

export const familyAccountTasks = {
  list: async (accountId: number, params?: AccountListParams): Promise<V2Paginated<AccountTaskRow>> => {
    const res = await apiV2.get<{ data: AccountTaskRow[]; meta: V2Meta }>(
      `/admin/family-accounts/${accountId}/tasks`,
      { params },
    );
    return unwrapPage(res);
  },

  create: async (accountId: number, payload: CreateAccountTaskPayload): Promise<AccountTaskRow> => {
    const res = await apiV2.post<Envelope<AccountTaskRow>>(
      `/admin/family-accounts/${accountId}/tasks`,
      payload,
    );
    return res.data.data;
  },

  update: async (id: number, payload: Partial<CreateAccountTaskPayload>): Promise<AccountTaskRow> => {
    const res = await apiV2.patch<Envelope<AccountTaskRow>>(`/admin/tasks/${id}`, payload);
    return res.data.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiV2.delete(`/admin/tasks/${id}`);
  },
};

/* ─── Notices ────────────────────────────────────────────────────────────── */

export interface CreateAccountNoticePayload {
  comment: string;
  /** Optional — Default: kein Personenbezug (Konto-weite Notiz). */
  user_id?: number | null;
  pinned?: boolean;
}

export const familyAccountNotices = {
  list: async (accountId: number, params?: AccountListParams): Promise<V2Paginated<AccountNoticeRow>> => {
    const res = await apiV2.get<{ data: AccountNoticeRow[]; meta: V2Meta }>(
      `/admin/family-accounts/${accountId}/notices`,
      { params },
    );
    return unwrapPage(res);
  },

  create: async (accountId: number, payload: CreateAccountNoticePayload): Promise<AccountNoticeRow> => {
    const res = await apiV2.post<Envelope<AccountNoticeRow>>(
      `/admin/family-accounts/${accountId}/notices`,
      payload,
    );
    return res.data.data;
  },

  update: async (id: number, payload: Partial<CreateAccountNoticePayload>): Promise<AccountNoticeRow> => {
    const res = await apiV2.patch<Envelope<AccountNoticeRow>>(`/admin/notices/${id}`, payload);
    return res.data.data;
  },

  pin: async (id: number): Promise<AccountNoticeRow> => {
    const res = await apiV2.patch<Envelope<AccountNoticeRow>>(`/admin/notices/${id}/pin`);
    return res.data.data;
  },

  delete: async (id: number): Promise<void> => {
    await apiV2.delete(`/admin/notices/${id}`);
  },
};

/* ─── Messages ───────────────────────────────────────────────────────────── */

export interface SendAccountMessagePayload {
  betreff: string;
  nachricht: string;
  smtp?: 'smtp' | 'smtp_pl';
  template?: number | null;
  absender?: string;
  anhang?: number[] | null;
}

export const familyAccountMessages = {
  list: async (accountId: number, params?: AccountListParams): Promise<V2Paginated<AccountMessageRow>> => {
    const res = await apiV2.get<{ data: AccountMessageRow[]; meta: V2Meta }>(
      `/admin/family-accounts/${accountId}/messages`,
      { params },
    );
    return unwrapPage(res);
  },

  /**
   * POST /v2/admin/users/{id}/messages — personenbezogen (E-Mail hängt an
   * einer Adresse). Löst tatsächlich eine Mail aus (SendMessage-Job), nicht
   * nur einen Verlaufs-Eintrag.
   */
  send: async (userId: number, payload: SendAccountMessagePayload): Promise<{ sent: boolean; user_id: number; account_id: number | null }> => {
    const res = await apiV2.post<Envelope<{ sent: boolean; user_id: number; account_id: number | null }>>(
      `/admin/users/${userId}/messages`,
      payload,
    );
    return res.data.data;
  },
};

/* ─── Protokoll (Konto-Umfang) ──────────────────────────────────────────── */

/**
 * Identische Zeilenform wie `ActivityProtocolEntry` (activityProtocol.ts) —
 * der Backend-Endpoint `forAccount()` ist eine Methode desselben Controllers
 * und liefert dieselben Felder (`entity_user_id`/`entity_konto`/
 * `entity_profile_id` bleiben erhalten, z. B. für den Navigations-Link in
 * `ProtokollTable::renderEntity()`), plus die Alias-Felder `user_id`/
 * `user_name` (Personenbezug — identisch zu `entity_user_id`/`entity_label`).
 */
export interface AccountProtocolEntry extends ActivityProtocolEntry {
  user_id: number | null;
  user_name: string | null;
}

export const familyAccountProtocol = {
  list: async (accountId: number, params?: AccountListParams): Promise<V2Paginated<AccountProtocolEntry>> => {
    const res = await apiV2.get<{ data: AccountProtocolEntry[]; meta: V2Meta }>(
      `/admin/family-accounts/${accountId}/protocol`,
      { params },
    );
    return unwrapPage(res);
  },
};
